import type { Theme } from '@emotion/react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { overflowBreakWord, shouldForwardProp } from '@hrworks/design-system/utilities';

const tooltipAnimation = keyframes({
  '0%': {
    opacity: 0,
    scale: 0.7,
    visibility: 'hidden',
  },
  '100%': {
    opacity: 1,
    scale: 1,
    visibility: 'visible',
  },
});

type TooltipContainerProps = {
  $isCustom?: boolean;
};

const tooltipContainerStyles = ({ theme, $isCustom }: TooltipContainerProps & { theme: Theme }) => [
  overflowBreakWord,
  !$isCustom && { boxShadow: theme.eclipseTier2Color.surface.elevation.shadow.overlay.boxShadow },
  {
    maxWidth: 300,
    zIndex: theme.variables.zIndex.popover,
    display: 'flex',
    flexDirection: 'column' as const,
    width: 'max-content',
    willChange: 'transform',
    pointerEvents: 'none' as const,
    backgroundColor: 'transparent',

    ...(!$isCustom && {
      padding: theme.eclipseTier1.spacing['12px'],
      borderRadius: theme.eclipseTier1.radius['12px'],
      backgroundColor: theme.eclipseTier2Color.background.neutral.bold.default,
      color: theme.eclipseTier2Color.text.inverse,
      gap: theme.eclipseTier1.spacing['8px'],
    }),
  },
];

/**
 * Styled Radix TooltipContent with animation and positioning via data-attributes.
 */
const RadixContent = styled(RadixTooltip.Content, { shouldForwardProp })<TooltipContainerProps>(
  ({ theme, $isCustom }) => [
    ...tooltipContainerStyles({ theme, $isCustom }),
    {
      '&[data-side="top"]': { transformOrigin: 'bottom' },
      '&[data-side="bottom"]': { transformOrigin: 'top' },
      '&[data-side="left"]': { transformOrigin: 'right' },
      '&[data-side="right"]': { transformOrigin: 'left' },

      "&[data-state='delayed-open'], &[data-state='instant-open']": {
        animation: `${tooltipAnimation} cubic-bezier(0.4, 0, 0.2, 1) ${theme.variables.animationDuration.normal} forwards`,
      },
    },
  ],
);

/** Styled tooltip container for direct usage (e.g. Slider custom tooltip). */
type TooltipContainerStyleProps = {
  playAnimation?: boolean;
  placement?: string;
  isCustom?: boolean;
};

const TooltipContainer = styled.div<TooltipContainerStyleProps>(({ theme, playAnimation, placement, isCustom }) => {
  const transformOriginDirection: Record<string, string> = {
    top: 'bottom',
    left: 'right',
    right: 'left',
    bottom: 'top',
  };

  return [
    ...tooltipContainerStyles({ theme, $isCustom: isCustom }),
    {
      transformOrigin: placement ? transformOriginDirection[placement.split('-')[0]] || 'bottom' : 'bottom',
      ...(playAnimation && {
        animation: `${tooltipAnimation} cubic-bezier(0.4, 0, 0.2, 1) ${theme.variables.animationDuration.normal} forwards`,
      }),
    },
  ];
});

export const S = {
  TooltipContainer,
  RadixContent,
} as const;
